import { ProductivityTag } from "@/components/ui/Tag.jsx";
import { ChartEmpty } from "@/components/charts/index.jsx";
import { avatarTone } from "@/lib/tokens.js";
import { formatDuration } from "@/lib/format.js";
import { cx } from "@/lib/cx.js";

/*
 * "Top Apps & Browsers", from dashboard.html.
 *
 * THE ROWS RUN EDGE TO EDGE. The scroll container is a direct child of the card
 * with no padding of its own — each row carries `px-4.5 py-3` and a divider, so
 * the hover highlight spans the full card and the scrollbar sits against its
 * edge. Wrapping this in the usual CardBody padded the list a second time,
 * inset the scrollbar and left the rows floating inside a box within a box.
 *
 * ONE BAR COLOUR. The bar is a ranking cue and its LENGTH is the information,
 * so every row is brand-coloured as in the markup. A per-app colour made the
 * colour look meaningful, and a green bar beside a green "productive" tag reads
 * as a second opinion about productivity rather than a measure of time.
 *
 * The coloured initial keeps `avatarTone(name)`: that hash is per name and
 * shared with the avatars, so an app is the same swatch wherever it appears.
 */
export default function TopApps({ apps = [] }) {
    // ChartEmpty rather than EmptyState: this card sits beside the trend
    // chart, and an empty pair should read as one gap rather than two
    // differently-shaped ones. The padding is here because the rows carry
    // their own and the card gives none.
    if (!apps.length) {
        return (
            <div className="p-4.5">
                <ChartEmpty message="No application usage in this range" />
            </div>
        );
    }

    const max = Math.max(1, ...apps.map((a) => a.totalTime || 0));

    return (
        <div className="flex max-h-[430px] flex-col overflow-y-auto" data-top-apps>
            {apps.map((app) => (
                <div
                    key={app.name}
                    data-app={app.name}
                    className="border-b border-slate-100 px-4.5 py-3 transition last:border-0 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/60"
                >
                    <div className="mb-2 flex min-w-0 items-center gap-2.5">
                        <span
                            className={cx(
                                "grid size-6 shrink-0 place-items-center rounded text-[10px] font-semibold text-white",
                                avatarTone(app.name),
                            )}
                            aria-hidden="true"
                        >
                            {app.name.charAt(0).toUpperCase()}
                        </span>

                        <span className="min-w-0 truncate text-[13px] font-medium">{app.name}</span>

                        <ProductivityTag status={app.productivityStatus} />

                        <span className="ml-auto shrink-0 font-mono text-[12.5px] font-semibold">
                            {formatDuration(app.totalTime)}
                        </span>
                    </div>

                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                        <div
                            className="h-full rounded-full bg-brand-500"
                            style={{ width: `${((app.totalTime || 0) / max) * 100}%` }}
                        />
                    </div>

                    {/*
                     * Muted, not brand-coloured. The markup renders this as a
                     * <button> in the brand colour, but it has no handler there
                     * and there is no per-app user list to open here — so the
                     * colour would be inviting a click that does nothing. It is
                     * a fact about the row, and it reads as one.
                     */}
                    <p className="mt-1.5 text-[11.5px] text-slate-400 dark:text-slate-500">
                        {app.userCount} {app.userCount === 1 ? "user" : "users"}
                    </p>
                </div>
            ))}
        </div>
    );
}
